import store from 'Redux/store';
import { getPersistedRecentlyViewedSlugs } from 'Api/RecentlyViewed/utility';
import { USER_RECOMMENDATIONS_FETCH, USER_RECOMMENDATIONS_LONG_TAIL_FETCH } from 'Redux/reducers/user-recommendations';
import { getUserRecommendations, getLongTailRecommendations } from 'Api/Recommendations/user-recommendations';

export async function fetchAndDispatchUserRecommendations() {
  const recentlyViewedSlugs = getPersistedRecentlyViewedSlugs();

  return getUserRecommendations(recentlyViewedSlugs)
    .then((hits) => {
      store.dispatch({ type: USER_RECOMMENDATIONS_FETCH, payload: hits });
    })
    .catch(() => {});
}

export async function fetchAndDispatchLongTailRecommendations() {
  return getLongTailRecommendations()
    .then((results) => {
      // TODO add any filters or formatting of new arrays in here.
      // The entire payload gets added to userRecommendations state

      const longTailRecommendations = results.filter(r => r._score > 20);

      store.dispatch({ type: USER_RECOMMENDATIONS_LONG_TAIL_FETCH,
        payload: {
          longTailRecommendations,
          longTailSlugs: longTailRecommendations.map(r => r.slug),
        } });
    })
    .catch(() => {});
}

// Password-reset login only sets `userId` (not `_id`) on the auth state.
const getCurrentUserId = () => store.getState().auth?._id || store.getState().auth?.userId;

let allUserRecommendationsInFlight = null;

// Both endpoints build the same per-user library on the backend, cached under
// one key. Running them one after the other lets the first populate that cache
// so the second hits it, rather than both missing and doing the lookup twice.
// Concurrent callers for the same user (e.g. initial load + login) share the
// in-flight promise; a different user (logout/login mid-flight) starts afresh.
export function fetchAndDispatchAllUserRecommendations() {
  const userId = getCurrentUserId();

  if (!allUserRecommendationsInFlight || allUserRecommendationsInFlight.userId !== userId) {
    const run = {
      userId,
      promise: fetchAndDispatchUserRecommendations()
        .catch(() => {})
        // Don't send the long-tail request as a different user if auth changed.
        .then(() => getCurrentUserId() === userId && fetchAndDispatchLongTailRecommendations())
        .catch(() => {})
        .finally(() => {
          if (allUserRecommendationsInFlight === run) {
            allUserRecommendationsInFlight = null;
          }
        }),
    };

    allUserRecommendationsInFlight = run;
  }

  return allUserRecommendationsInFlight.promise;
}

// Resolves once any in-flight run above has finished (immediately if none), so
// other callers can avoid hitting the backend while its cache is still cold.
export function waitForUserRecommendations() {
  return allUserRecommendationsInFlight?.promise || Promise.resolve();
}

// Same breakpoint as the secondary navbar, where the mega menu's recommendations tab lives.
const DESKTOP_QUERY = '(min-width: 992px)';

const isDesktopViewport = () => window.matchMedia(DESKTOP_QUERY).matches;

// For the page-load and login fetches. Below desktop nothing shows these up front (the mega menu is hidden), so
// consumers that do (account overview) call fetchUserRecommendationsIfMissing when they mount instead.
export function prefetchAllUserRecommendations() {
  if (!isDesktopViewport()) {
    return Promise.resolve();
  }

  return fetchAndDispatchAllUserRecommendations();
}

export function fetchUserRecommendationsIfMissing() {
  if (store.getState().userRecommendations?.recommendations) {
    return Promise.resolve();
  }

  return fetchAndDispatchAllUserRecommendations();
}
